import { useEffect } from "react";
import { Provider } from "react-redux";
import { ToastProvider } from "react-toast-notifications";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastContainer } from "react-toastify";

import AppRouter from "./Route/AppRouter";
import SentryContextSync from "./components/observability/SentryContextSync";
import GlobalProcessingOverlay from "./components/desktop/processing/GlobalProcessingOverlay";
import store, { saveState, setupStatePersistence } from "./redux/store";
import useUserStore from "./stores/useUserStore";
import ApiClient from "./Route/ApiClient";
import { setupCrossTabSync } from "./services/crossTabSync";
import { queryClient } from "./services/queryClient";

import "react-phone-number-input/style.css";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";

const App = () => {
	ApiClient.setupInterceptors(store);

	useEffect(() => {
		const authUser = store.getState().auth?.user;
		if (authUser) {
			useUserStore.getState().setUserFromAuth(authUser);
		}

		const teardownPersistence = setupStatePersistence();
		const teardownCrossTabSync = setupCrossTabSync();

		const saveOnHide = () => {
			if (document.visibilityState === "hidden") {
				saveState();
			}
		};

		window.addEventListener("pagehide", saveState);
		document.addEventListener("visibilitychange", saveOnHide);

		return () => {
			teardownPersistence();
			teardownCrossTabSync();
			window.removeEventListener("pagehide", saveState);
			document.removeEventListener("visibilitychange", saveOnHide);
			saveState();
		};
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			<Provider store={store}>
				<SentryContextSync />
				<ToastProvider autoDismiss autoDismissTimeout={6000}>
					<AppRouter />
					<ReactQueryDevtools />
				</ToastProvider>
				<ToastContainer />
				<GlobalProcessingOverlay />
			</Provider>
		</QueryClientProvider>
	);
};

export default App;
